/**
 * Authentication Provider
 *
 * Manages authentication state and provides auth context
 * Integrates with Firebase Auth and user/tenant management
 */

import { createContext, useContext, useEffect, useState, PropsWithChildren, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  availableFactoriesAtom,
  currentFactoryIdAtom,
  resetFactoryStateAtom,
} from '@/stores/atoms';
import { Factory } from '@/schemas/factory';
import { Tenant } from '@/schemas/common';
import { User, UserTenantAccess, type Permission } from '@/schemas/auth';
import { handleQueryError, AppError, createAuthError } from '@/lib/error-handling';
import { notify } from '@/lib/notify';
import {
  fetchWithAuth,
  registerUnauthorizedHandler,
  unregisterUnauthorizedHandler,
  clearAuthToken,
  clearSelectedTenantId,
} from '@/lib/api-client';
import { listFactories } from '@/services/factory.service';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { setSentryUser } from '@/lib/sentry';
import {
  type OAuthProviderType,
  signInWithGoogleProvider,
  signInWithMicrosoftProvider,
  handleOAuthError,
  extractTenantFromInvitation,
} from '@/contexts/auth/oauth-providers';
import {
  setAuthPersistence,
  getStoredAuthToken,
  clearAuthStorage,
  refreshAuthFromBackend,
  completeServerAuthentication,
  tenantAccessToTenant,
} from '@/contexts/auth/session-manager';
import { hasEffectivePermission } from '@/lib/permissions';

// Auth context interface
interface AuthContextValue {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  currentTenantAccess: UserTenantAccess | null;
  tenant: Tenant | null;

  // Available factories for user
  availableFactories: Factory[];

  // Auth actions
  signIn: (credentials: { email: string; password: string; rememberMe?: boolean }) => Promise<void>;
  signInWithGoogle: (rememberMe?: boolean) => Promise<void>;
  signInWithMicrosoft: (rememberMe?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  refreshAuth: (forceRefresh?: boolean) => Promise<void>;

  // Factory selection
  selectFactory: (factorySelection: { factoryId: string }) => Promise<void>;

  // OAuth-specific methods
  extractTenantFromInvitation: () => string | null;
  completeServerLogin: (options: {
    idToken: string;
    rememberMe?: boolean;
    provider?: OAuthProviderType;
    tenantId?: string;
  }) => Promise<void>;

  // Permission helpers
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;

  // Error state
  authError: AppError | null;
  clearAuthError: () => void;
}

// Create context with default values
const AuthContext = createContext<AuthContextValue | null>(null);

// Custom hook to use auth context
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Safe variant that returns null when outside AuthProvider (for providers that
// may render before auth context is ready, e.g. I18nProvider during HMR).
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

// Auth provider component
export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const { t } = useTranslation();

  // Local state for auth loading and errors
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<AppError | null>(null);

  // Simplified user and tenant state (local for now)
  const [user, setUser] = useState<User | null>(null);
  const [currentTenantAccess, setCurrentTenantAccess] = useState<UserTenantAccess | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);

  // Atom setters and getters
  const setAvailableFactories = useSetAtom(availableFactoriesAtom);
  const setCurrentFactoryId = useSetAtom(currentFactoryIdAtom);
  const resetFactoryState = useSetAtom(resetFactoryStateAtom);
  const availableFactories = useAtomValue(availableFactoriesAtom);
  const currentFactoryId = useAtomValue(currentFactoryIdAtom);

  // Derived state
  const isAuthenticated = !!user && !!tenant;

  const clearUserData = (): void => {
    setUser(null);
    setCurrentTenantAccess(null);
    setTenant(null);
    setAvailableFactories([]);
    resetFactoryState();
    setSentryUser(null);
  };

  const refreshAuth = async (forceRefresh = false): Promise<void> => {
    try {
      const { user: freshUser, tenantAccess } = await refreshAuthFromBackend({ forceRefresh });
      setUser(freshUser);
      setCurrentTenantAccess(tenantAccess);
      setTenant(tenantAccessToTenant(tenantAccess));
      setSentryUser({
        id: freshUser.uid,
        email: freshUser.email ?? undefined,
        tenantId: tenantAccess?.tenantId ?? undefined,
      });
    } catch (error) {
      logger.error('🔍 refreshAuth: Error:', error);
      clearUserData();
      throw error;
    }
  };

  const selectFactory = async (options: { factoryId: string }): Promise<void> => {
    const { factoryId } = options;
    const hasFactoryAccess = currentTenantAccess?.factoryAccess?.some(
      fa => fa.factoryId === '*' || fa.factoryId === factoryId
    );
    if (!user || !hasFactoryAccess) {
      throw createAuthError('User does not have access to this factory');
    }

    try {
      setIsLoading(true);
      resetFactoryState();
      setCurrentFactoryId(factoryId);
    } catch (error) {
      const appError = handleQueryError(error, ['factory', 'select']);
      setAuthError(appError);
      throw appError;
    } finally {
      setIsLoading(false);
    }
  };

  const loadUserFactories = async (): Promise<void> => {
    if (!user || !currentTenantAccess) {
      return;
    }

    try {
      const { factories: allFactories } = await listFactories({ page: 1, limit: 100 });

      const userFactoryAccess = currentTenantAccess.factoryAccess || [];
      const hasWildcardAccess =
        userFactoryAccess.length === 0 ||
        userFactoryAccess.some(access => access.factoryId === '*');

      const userFactories = hasWildcardAccess
        ? allFactories
        : allFactories.filter(factory =>
            userFactoryAccess.some(access => access.factoryId === factory.id)
          );

      setAvailableFactories(userFactories);

      // Auto-select factory if user has access to only one.
      if (userFactories.length === 1 && !currentFactoryId) {
        resetFactoryState();
        setCurrentFactoryId(userFactories[0].id);
      }
    } catch (error) {
      const appError = handleQueryError(error, ['factories', 'load']);
      setAuthError(appError);
      notify.error(t('factory.errors.load_failed'));
    }
  };

  const initializeAuth = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setAuthError(null);

      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      try {
        await setAuthPersistence(rememberMe);
      } catch (error) {
        logger.error('Failed to set Firebase persistence:', error);
      }

      const token = getStoredAuthToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      await refreshAuth();
    } catch (error) {
      const appError = handleQueryError(error, ['auth', 'initialize']);
      setAuthError(appError);
      clearAuthStorage();
      clearUserData();
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  // Register 401 unauthorized handler
  useEffect(() => {
    registerUnauthorizedHandler(() => {
      clearUserData();
      clearAuthToken();
      clearSelectedTenantId();
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('invitationTenantId');
      notify.error(t('auth.session_expired'));
    });

    return (): void => {
      unregisterUnauthorizedHandler();
    };
  }, []);

  // Monitor auth state changes
  useEffect(() => {
    if (user) {
      loadUserFactories();
    }
  }, [user]);

  const completeServerLogin = async (options: {
    idToken: string;
    rememberMe?: boolean;
    provider?: OAuthProviderType;
    tenantId?: string;
  }): Promise<void> => {
    const { idToken, rememberMe = false, provider, tenantId } = options;

    try {
      const {
        user: freshUser,
        tenants,
        isFirstLogin,
        message,
      } = await completeServerAuthentication({
        idToken,
        rememberMe,
        provider,
        tenantId,
      });

      const [firstTenantAccess] = tenants;
      setUser(freshUser);
      setCurrentTenantAccess(firstTenantAccess);
      setTenant(tenantAccessToTenant(firstTenantAccess));
      setSentryUser({
        id: freshUser.uid,
        email: freshUser.email ?? undefined,
        tenantId: firstTenantAccess?.tenantId ?? undefined,
      });

      if (isFirstLogin) {
        notify.success(message || t('auth.welcome_new_account'));
      }

      await loadUserFactories();
    } catch (error) {
      if (error instanceof AppError && error.statusCode === 400) {
        if (error.message?.includes('First-time login requires tenantId')) {
          const specificError = createAuthError(
            'Please use an invitation link to sign up for the first time, or contact your administrator for access.'
          );
          setAuthError(specificError);
          throw specificError;
        }
      }

      const appError = handleQueryError(error, ['auth', 'oauth', 'complete']);
      setAuthError(appError);
      throw appError;
    }
  };

  const signIn = async (credentials: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }): Promise<void> => {
    const { email, password, rememberMe = false } = credentials;
    try {
      setIsLoading(true);
      setAuthError(null);

      try {
        await setAuthPersistence(rememberMe);
      } catch (error) {
        logger.error('Failed to set Firebase persistence:', error);
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();

      const { user: freshUser, tenants } = await completeServerAuthentication({
        idToken,
        rememberMe,
        email,
      });

      const [firstTenantAccess] = tenants;
      setUser(freshUser);
      setCurrentTenantAccess(firstTenantAccess);
      setTenant(tenantAccessToTenant(firstTenantAccess));
      setSentryUser({
        id: freshUser.uid,
        email: freshUser.email ?? undefined,
        tenantId: firstTenantAccess?.tenantId ?? undefined,
      });

      await loadUserFactories();
    } catch (error) {
      const appError = handleQueryError(error, ['auth', 'signin']);
      setAuthError(appError);
      throw appError;
    } finally {
      setIsLoading(false);
    }
  };

  // Google OAuth sign in
  const signInWithGoogle = async (
    rememberMe: boolean = false,
    tenantId?: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      setAuthError(null);

      const invitationTenantId = tenantId || extractTenantFromInvitation();

      try {
        await setAuthPersistence(rememberMe);
      } catch (error) {
        logger.error('Failed to set Firebase persistence:', error);
      }

      const result = await signInWithGoogleProvider();
      const idToken = await result.user.getIdToken();

      await completeServerLogin({
        idToken,
        tenantId: invitationTenantId || undefined,
        provider: 'google',
        rememberMe,
      });
    } catch (error) {
      const appError = handleOAuthError(error as { code?: string; message?: string }, 'google');
      setAuthError(appError);
      throw appError;
    } finally {
      setIsLoading(false);
    }
  };

  // Microsoft OAuth sign in
  const signInWithMicrosoft = async (
    rememberMe: boolean = false,
    tenantId?: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      setAuthError(null);

      const invitationTenantId = tenantId || extractTenantFromInvitation();

      try {
        await setAuthPersistence(rememberMe);
      } catch (error) {
        logger.error('Failed to set Firebase persistence:', error);
      }

      const result = await signInWithMicrosoftProvider();
      const idToken = await result.user.getIdToken();

      await completeServerLogin({
        idToken,
        tenantId: invitationTenantId || undefined,
        provider: 'microsoft',
        rememberMe,
      });
    } catch (error) {
      const appError = handleOAuthError(error as { code?: string; message?: string }, 'microsoft');
      setAuthError(appError);
      throw appError;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      setIsLoading(true);

      try {
        await fetchWithAuth('/api/auth/logout', { method: 'POST' });
      } catch {
        notify.warning(t('auth.signout_partial_failure'));
      }

      clearUserData();
      clearAuthToken();
      clearSelectedTenantId();
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('invitationTenantId');
    } catch (error) {
      const appError = handleQueryError(error, ['auth', 'signout']);
      setAuthError(appError);
    } finally {
      setIsLoading(false);
    }
  };

  const clearAuthError = (): void => {
    setAuthError(null);
  };

  const contextValue: AuthContextValue = {
    isAuthenticated,
    isLoading,
    user,
    currentTenantAccess,
    tenant,
    availableFactories,
    signIn,
    signInWithGoogle,
    signInWithMicrosoft,
    signOut,
    refreshAuth,
    selectFactory,
    extractTenantFromInvitation,
    completeServerLogin,

    // Permission helpers
    hasPermission: (permission: Permission): boolean =>
      currentTenantAccess
        ? hasEffectivePermission(currentTenantAccess.permissions, permission)
        : false,

    hasAnyPermission: (permissions: Permission[]): boolean =>
      currentTenantAccess
        ? permissions.some(p => hasEffectivePermission(currentTenantAccess.permissions, p))
        : false,
    hasAllPermissions: (permissions: Permission[]): boolean =>
      currentTenantAccess
        ? permissions.every(p => hasEffectivePermission(currentTenantAccess.permissions, p))
        : false,

    // Error handling
    authError,
    clearAuthError,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
